import { InjectionToken } from '@angular/core';

/**
 * Global SEO configuration provided at the application level.
 */
export interface SeoConfig {
  /** Canonical origin of the site, without a trailing slash, e.g. `https://www.imagise.app` */
  baseUrl: string;
  /** Languages exposed to search engines (hreflang, sitemap). */
  supportedLangs: string[];
  /** Language served at the unprefixed URL; its prefixed URL is canonicalized to the unprefixed one. */
  defaultLang: string;
  /** Site-wide name used in titles and structured data. */
  siteName: string;
  /** Absolute or root-relative URL of the default social sharing image. */
  defaultImage: string;
}

export const SEO_CONFIG = new InjectionToken<SeoConfig>('SEO_CONFIG');

/**
 * Context passed to JSON-LD builders when a page is rendered.
 */
export interface JsonLdContext {
  /** Canonical URL of the current page. */
  url: string;
  /** Site origin without a trailing slash. */
  baseUrl: string;
  /** Active language code. */
  lang: string;
  /** Global SEO configuration. */
  config: SeoConfig;
  /** Resolves an i18n key to the translated string for the active language. */
  t: (key: string) => string;
}

export type JsonLdBuilder = (ctx: JsonLdContext) => Record<string, unknown>;

/**
 * Per-page SEO description attached to a route via `data: { seo: ... }`.
 * All textual fields are i18n keys, so meta tags are localized.
 */
export interface PageSeo {
  /** i18n key for the document title. */
  titleKey: string;
  /** i18n key for the meta description. */
  descriptionKey: string;
  /** Optional i18n key for meta keywords. */
  keywordsKey?: string;
  /** Route path without the language prefix, e.g. `''` or `'crop-image'`. */
  path: string;
  /** Structured data builders rendered as `application/ld+json` scripts. */
  jsonLd?: JsonLdBuilder[];
}
